/**
 * `<SeatKnockOnHost/>` — the knock-on of a ladder seat, on the shared review
 * screen (#1964, slice 3D of #1947).
 *
 * **Why the lead comes:** they opened a short seat to someone who already
 * sits on another ward that session, and want to see what moving them
 * leaves behind before anything is written. **What they see first:** two
 * rows — the person moving (their ward struck through, the short seat
 * outlined) and who covers the ward they leave, with the other free people
 * for it underneath. **What they adjust straight away:** reject the
 * backfill (the ward is left short) or hand it to someone else, then Apply.
 *
 * Mounted once by the app shell, in the right-panel overlay (480 px, the
 * ladder's width). The ladder closes as it opens — one drawer slot. Apply →
 * `POST …/shortfalls/seat/apply`; a 409 lists the cells that moved; the
 * team's scheduling rules objecting offers *Apply anyway*. After an apply
 * the panel closes and a toast says what landed.
 */
import { useEffect, useMemo, useState } from "react";

import { ApiError } from "../../api/client";
import { type SeatApplyResponse, useApplySeat } from "../../api/useSeatKnockOn";
import {
  type ReviewChange,
  type ReviewDecision,
  type ReviewPin,
  reviewDate,
  reviewSession,
} from "../../lib/changeReview";
import { type SeatKnockOnRequest, useSeatKnockOnStore } from "../../store/seatKnockOnRequest";
import { HeldResolveDiscard } from "../HeldResolveDiscard";
import { CycleSurfaceModal } from "../cycle/CycleSurfaceModal";
import { rulesObjecting } from "../cycle/ShortfallOptionsModal";
import { Button, Toast } from "../ui";
import { ChangeReviewPanel } from "./ChangeReviewPanel";

const TOAST_MS = 4000;

/** `Seated Rex Reg · Bea Reg covers 56` / `… · 56 AM is now short`. */
export function seatKnockOnToast(name: string, res: SeatApplyResponse): string {
  const vacated = res.vacated ?? null;
  if (res.backfill_display_name && vacated) {
    return `Seated ${name} · ${res.backfill_display_name} covers ${vacated.station}`;
  }
  if (vacated?.short) {
    return `Seated ${name} · ${vacated.station} ${reviewSession(vacated.session)} is now short`;
  }
  return `Seated ${name}`;
}

function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    const detail = (err.body as { error?: { detail?: unknown } } | null)?.error?.detail;
    if (detail && typeof detail === "object" && "message" in detail) {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === "string" && message) return message;
    }
    return err.detail;
  }
  return err instanceof Error ? err.message : "Not applied.";
}

function SeatKnockOnPanel({
  request,
  onApplied,
  onDiscard,
}: {
  request: SeatKnockOnRequest;
  onApplied: (toast: string) => void;
  onDiscard: () => void;
}) {
  const apply = useApplySeat(request.cycleId);
  const { preview, target, displayName } = request;
  // The rules objected to the last Apply: its decision, to re-send acknowledged.
  const [rules, setRules] = useState<{ messages: string[]; decision: ReviewDecision } | null>(null);
  const [anywayError, setAnywayError] = useState<string | null>(null);

  const changes = useMemo<ReviewChange[]>(
    () =>
      preview.changes.map((c) => ({
        membership_id: c.membership_id,
        display_name: c.display_name,
        date: c.date,
        session: c.session,
        before_station: c.before_station ?? null,
        after_station: c.after_station ?? null,
        alternatives: (c.alternatives ?? []).map((a) => ({
          membership_id: a.membership_id,
          display_name: a.display_name,
          reason: a.reason ?? undefined,
        })),
      })),
    [preview],
  );
  const pins = useMemo<ReviewPin[]>(
    () =>
      preview.pins.map((p) => ({
        membership_id: p.membership_id,
        date: p.date,
        station: p.station,
        session: p.session,
      })),
    [preview],
  );
  const vacated = preview.vacated ?? null;
  const hasBackfill = changes.some(
    (c) => c.membership_id !== target.membershipId && c.after_station === vacated?.station,
  );
  const notFree = (preview.backfill ?? []).filter((b) => b.not_free_reason);

  const run = async (decision: ReviewDecision, acknowledgeRules: boolean) => {
    const res = await apply.mutateAsync({
      target,
      preview,
      decision: { skip: decision.skip, seat: decision.seat },
      acknowledgeRules,
    });
    onApplied(seatKnockOnToast(displayName, res));
  };

  const onApply = async (decision: ReviewDecision) => {
    setRules(null);
    setAnywayError(null);
    try {
      await run(decision, false);
    } catch (err) {
      const objecting = rulesObjecting(err);
      if (!objecting) throw err;
      setRules({ messages: objecting, decision });
      throw new Error(
        `Goes against the team's scheduling rules: ${
          objecting.length > 0 ? objecting.join(" ") : "see the roster check"
        }`,
      );
    }
  };

  return (
    <div className="space-y-3" data-testid="seat-knock-on">
      <ChangeReviewPanel
        changes={changes}
        pins={pins}
        onApply={onApply}
        onDiscard={onDiscard}
        describeError={errorText}
        firstMembershipId={target.membershipId}
        testid="seat-knock-on-review"
      />
      {/* #2263 — a held re-solve changes this day: discard it, then apply again. */}
      <HeldResolveDiscard cycleId={request.cycleId} error={apply.error} then="Apply again." />
      {vacated?.short && !hasBackfill ? (
        <p data-testid="seat-knock-on-left-short" className="text-xs text-danger">
          {vacated.station} {reviewSession(vacated.session)} is left short · nobody free
        </p>
      ) : null}
      {rules ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            data-testid="seat-knock-on-anyway"
            disabled={apply.isPending}
            onClick={() => {
              setAnywayError(null);
              run(rules.decision, true).catch((err: unknown) => setAnywayError(errorText(err)));
            }}
          >
            Apply anyway
          </Button>
          {anywayError ? (
            <p role="alert" className="text-xs text-danger">
              {anywayError}
            </p>
          ) : null}
        </div>
      ) : null}
      {notFree.length > 0 && vacated ? (
        <ul className="space-y-0.5 border-t border-border pt-1.5" data-testid="seat-knock-on-busy">
          <li className="text-[10px] font-semibold uppercase tracking-wide text-fg-faint">
            Not free for {vacated.station} {reviewSession(vacated.session)}
          </li>
          {notFree.map((b) => (
            <li key={b.membership_id} className="text-[11px] text-fg-subtle">
              {b.not_free_reason}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function SeatKnockOnHost() {
  const request = useSeatKnockOnStore((s) => s.request);
  const close = useSeatKnockOnStore((s) => s.closeSeatKnockOn);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => window.clearTimeout(t);
  }, [toast]);

  const t = request?.target;
  return (
    <>
      <CycleSurfaceModal
        open={request !== null}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        title={request ? `Open seat to ${request.displayName}` : "Open seat"}
        {...(t
          ? { description: `${reviewDate(t.date)} · ${t.station} ${reviewSession(t.session)}` }
          : {})}
        testid="seat-knock-on-surface"
        panelWidth="lg"
      >
        {request ? (
          <SeatKnockOnPanel
            // A fresh preview starts the review afresh.
            key={request.preview.token}
            request={request}
            onDiscard={close}
            onApplied={(text) => {
              close();
              setToast(text);
            }}
          />
        ) : null}
      </CycleSurfaceModal>
      {/* #2172 — slides up from the corner, and back down. */}
      <Toast message={toast} testid="seat-knock-on-toast" />
    </>
  );
}

export default SeatKnockOnHost;
